import type { MerchantAppReturn } from "@redotech/redo-model/return";
import { BasicIndex, parseLoadSubsetOptions } from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import {
  supportTicketQueryScopeKey,
  type SupportTicketQueryScope,
} from "./support-ticket-query-keys";
import {
  getEmailFilterValue,
  type OrderWithPickStatus,
  type SupportTicketRelatedRecordsApi,
} from "./support-ticket-related-records-requests";

const FIVE_MINUTES_MS = 5 * 60 * 1000;
export const SUPPORT_TICKET_RELATED_RECORD_LIMIT = 10;

export const supportTicketOrderQueryKeys = {
  all: ["orders"] as const,
  collection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketOrderQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
    ] as const,
  byEmail: (email: string, scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketOrderQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
      "byEmail",
      email,
    ] as const,
};

export const supportTicketReturnQueryKeys = {
  all: ["returns"] as const,
  collection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketReturnQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
    ] as const,
  byEmail: (email: string, scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketReturnQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
      "byEmail",
      email,
    ] as const,
};

export function createOrdersCollection({
  queryClient,
  api,
  scope,
}: {
  queryClient: QueryClient;
  api: SupportTicketRelatedRecordsApi;
  scope?: SupportTicketQueryScope;
}) {
  return createCollection<OrderWithPickStatus, string>(
    queryCollectionOptions({
      queryKey: supportTicketOrderQueryKeys.collection(scope),
      queryFn: async (ctx): Promise<OrderWithPickStatus[]> => {
        const { filters, limit } = parseLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        const email = getEmailFilterValue(filters, "shopify", true);
        return email ? api.getOrdersByEmail(email, { limit }) : [];
      },
      queryClient,
      getKey: (item) => item._id,
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
    }),
  );
}

export function createReturnsCollection({
  queryClient,
  api,
  scope,
}: {
  queryClient: QueryClient;
  api: SupportTicketRelatedRecordsApi;
  scope?: SupportTicketQueryScope;
}) {
  return createCollection<MerchantAppReturn, string>(
    queryCollectionOptions({
      queryKey: supportTicketReturnQueryKeys.collection(scope),
      queryFn: async (ctx): Promise<MerchantAppReturn[]> => {
        const { filters, limit } = parseLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        const email = getEmailFilterValue(filters, "shipping_address", false);
        return email ? api.getReturnsByEmail(email, { limit }) : [];
      },
      queryClient,
      getKey: (item) => item._id,
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
    }),
  );
}

export type OrdersCollection = ReturnType<typeof createOrdersCollection>;
export type ReturnsCollection = ReturnType<typeof createReturnsCollection>;

export {
  getEmailFilterValue,
  type OrderWithPickStatus,
  type SupportTicketRelatedRecordsApi,
} from "./support-ticket-related-records-requests";
